import React, { useState, useEffect, useRef } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, PointElement, LineElement } from 'chart.js';
import Papa from 'papaparse';
import '../styles/dashboard.css';
import '../styles/navbar.css';

// Register necessary ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement
);

const Dashboard = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [activeProjects, setActiveProjects] = useState(0);
    const [volunteers, setVolunteers] = useState(0);
    const [futureVolunteers, setFutureVolunteers] = useState(0);
    const [researchPapers, setResearchPapers] = useState(0);
    const chartRef = useRef(null);

    // Fetch research data from Google Sheets
    const fetchResearchData = async () => {
        try {
            const csvURL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vRVkpQeiOG-mhuy5HurM4dU-bYZTcQSHKybqTrYyai0yMbI8dEM60Z1GAMA7q-zJnWRS593cGKUxFjt/pub?gid=0&single=true&output=csv";
            const response = await fetch(csvURL);
            const text = await response.text();

            Papa.parse(text, {
                header: true,
                dynamicTyping: true,
                complete: (result) => {
                    setActiveProjects(result.data.length);
                    setResearchPapers(result.data.length);
                },
                error: (error) => {
                    console.error('Error parsing research CSV:', error);
                }
            });
        } catch (error) {
            console.error('Error fetching research data:', error);
        }
    };

    // Fetch volunteers data from Google Sheets
    const fetchVolunteersData = async () => {
        try {
            const csvURL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vQcv-rWLtvO5o3mr5rw88jJkBDH58-KKWN1sDHz10Uf6NBa5cbgIY5NeUSTSr9w_GrOAf9VTj6KQMie/pub?gid=0&single=true&output=csv";
            const response = await fetch(csvURL);
            const text = await response.text();

            Papa.parse(text, {
                header: true,
                dynamicTyping: true,
                complete: (result) => {
                    setVolunteers(result.data.length);
                },
                error: (error) => {
                    console.error('Error parsing volunteers CSV:', error);
                }
            });
        } catch (error) {
            console.error('Error fetching volunteers data:', error);
        }
    };

    // Fetch future volunteers data from Google Sheets
    const fetchFutureVolunteersData = async () => {
        try {
            const csvURL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vQZAMagACXZFLNVuPpOHF6OV-bHVPFzcmuYNRfnGiO0jydEA8YrmFm3Jfhb-zzcQWSd5jkzjZ7dTuXx/pub?output=csv";
            const response = await fetch(csvURL);
            const text = await response.text();

            Papa.parse(text, {
                header: true,
                dynamicTyping: true,
                complete: (result) => {
                    setFutureVolunteers(result.data.length);
                },
                error: (error) => {
                    console.error('Error parsing future volunteers CSV:', error);
                }
            });
        } catch (error) {
            console.error('Error fetching future volunteers data:', error);
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchResearchData();
        fetchVolunteersData();
        fetchFutureVolunteersData();
    }, []);

    // Chart data for the bar chart (Research Progress)
    const researchProgressData = {
        labels: ['January', 'February', 'March', 'April', 'May'],
        datasets: [
            {
                label: 'Research Progress (%)',
                data: [20, 40, 60, 80, 100],
                backgroundColor: 'rgba(0, 247, 255, 0.5)', // Neon cyan
                borderColor: 'rgba(0, 247, 255, 1)',
                borderWidth: 1,
                tension: 0.4,
            },
        ],
    };

    // Options for bar chart
    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top', labels: { color: '#e0f7fa' } },
            tooltip: { enabled: true, mode: 'index', intersect: false },
        },
        scales: {
            x: { ticks: { color: '#e0f7fa' }, grid: { color: 'rgba(0, 247, 255, 0.1)' } },
            y: { ticks: { color: '#e0f7fa' }, grid: { color: 'rgba(0, 247, 255, 0.1)' } },
        },
        animations: {
            y: { duration: 1000, easing: 'easeOutQuad', from: 0 },
        },
    };

    // Chart data for the line chart (Publications Over Time)
    const publicationsData = {
        labels: ['January', 'February', 'March', 'April', 'May'],
        datasets: [
            {
                label: 'Publications',
                data: [2, 4, 6, 8, 10],
                borderColor: 'rgba(255, 64, 129, 1)', // Neon pink
                backgroundColor: 'rgba(255, 64, 129, 0.2)',
                borderWidth: 2,
                fill: false,
            },
        ],
    };

    // Options for line chart
    const lineChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { beginAtZero: true, ticks: { color: '#e0f7fa' }, grid: { color: 'rgba(0, 247, 255, 0.1)' } },
            y: { beginAtZero: true, ticks: { color: '#e0f7fa' }, grid: { color: 'rgba(0, 247, 255, 0.1)' } },
        },
        plugins: {
            legend: { display: true, position: 'top', labels: { color: '#e0f7fa' } },
            tooltip: { enabled: true },
        },
        animations: {
            tension: { duration: 1000, easing: 'easeOutBounce', from: 0, to: 0.4 },
        },
    };

    // Chart cleanup
    useEffect(() => {
        const chartInstance = chartRef.current?.chartInstance;
        return () => {
            if (chartInstance) {
                chartInstance.destroy();
            }
        };
    }, []);

    // Toggle sidebar state
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Handle logout
    const handleLogout = () => {
        window.location.href = '/';
    };

    return (
        <div className="dashboard-container">
            <div className="particles">
                <div className="particle"></div>
                <div className="particle"></div>
                <div className="particle"></div>
                <div className="particle"></div>
                <div className="particle"></div>
            </div>
            <aside className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
                <div className="sidebar-header">
                    <h1 className="logo" data-text="Innovathrive">Innovathrive</h1>
                    <button onClick={toggleSidebar} className="toggle-button">
                        {isSidebarOpen ? (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        ) : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        )}
                    </button>
                </div>
                <nav id="nav-bar">
                    <div className="nav-particles">
                        <div className="nav-particle"></div>
                        <div className="nav-particle"></div>
                        <div className="nav-particle"></div>
                    </div>
                    <div id="nav-header">
                        <a id="nav-title" href="/dashboard" data-text="Dashboard">Dashboard</a>
                        <hr />
                    </div>
                    <div id="nav-content">
                        <div className={`nav-button ${window.location.pathname === '/dashboard' ? 'active' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />
                            </svg>
                            <a href="/dashboard" data-text="Dashboard">Dashboard</a>
                        </div>
                        <div className={`nav-button ${window.location.pathname === '/research' ? 'active' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <a href="/research" data-text="Research Reviewing Committee">Research</a>
                        </div>
                        <div className={`nav-button ${window.location.pathname === '/volunteers' ? 'active' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                            <a href="/volunteers" data-text="Volunteers Board">Volunteers Board</a>
                        </div>
                        <div className={`nav-button ${window.location.pathname === '/futurevolunteers' ? 'active' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="8.5" cy="7" r="4" />
                                <path d="M20 8v6M23 11h-6" />
                            </svg>
                            <a href="/futurevolunteers" data-text="Future Volunteers">Future Volunteers</a>
                        </div>
                        <div className={`nav-button ${window.location.pathname === '/USAID' ? 'active' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                            <a href="/USAID" data-text="USAID">USAID</a>
                        </div>
                        <hr />
                    </div>
                </nav>
            </aside>
            <main className="main-content">
                <header className="dashboard-header">
                    <div className="header-title">
                        <h2>Welcome to Innovathrive Dashboard</h2>
                        <p>Your hub for academic research and community empowerment.</p>
                    </div>
                    <div className="header-actions">
                        <button className="notification-button">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                            </svg>
                        </button>
                        <button className="profile-button">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                            </svg>
                        </button>
                        <button onClick={handleLogout} className="logout-button">Logout</button>
                    </div>
                </header>
                <section className="dashboard-widgets">
                    <div className="widget">
                        <h3>Active Projects</h3>
                        <p>{activeProjects} ongoing projects</p>
                    </div>
                    <div className="widget">
                        <h3>Volunteers</h3>
                        <p>{volunteers} active members</p>
                    </div>
                    <div className="widget">
                        <h3>Future Volunteers</h3>
                        <p>{futureVolunteers} registrations</p>
                    </div>
                    <div className="widget">
                        <h3>Research Papers</h3>
                        <p>{researchPapers} published papers</p>
                    </div>
                </section>
                <section className="dashboard-charts">
                    <div className="chart-container">
                        <h3>Research Progress Overview</h3>
                        <Bar ref={chartRef} data={researchProgressData} options={barChartOptions} />
                    </div>
                    <div className="chart-container">
                        <h3>Publications Over Time</h3>
                        <Line data={publicationsData} options={lineChartOptions} />
                    </div>
                </section>
                <section className="recent-activity">
                    <h3>Recent Activity</h3>
                    <ul>
                        <li>New research paper published: "Impact of Renewable Energy"</li>
                        <li>Collaboration established with LAU University</li>
                        <li>Volunteer John Doe joined the research team</li>
                        <li>New project "Sustainable Agriculture" initiated</li>
                    </ul>
                </section>
            </main>
        </div>
    );
};

export default Dashboard;